/*
 * Where an owner lands after signing in when they came from a setup email. The emails link to the plain owner paths below (no query, no
 * parameter). When a signed-out visitor, or an owner who has not finished first run, opens one of EXACTLY these paths, only a fixed KEY is kept (in this
 * tab's sessionStorage, for 30 minutes (renewed while in first run, never beyond 2 hours from when the link was opened), used once, cleared on sign-out). The path to go to is rebuilt here from the key; an address or path
 * from the URL is never stored and never used for any other redirect.
 *
 * KNOWN LIMIT (decided, not an oversight): a key that nothing consumes stays for up to 30 minutes: the owner opened the link signed out and abandoned it,
 * left first run with the step-1 back arrow, or a consent return came first. In those cases a later sign-in as an owner in the SAME tab within that
 * window lands on the link instead of the dashboard (and a later "Finish setup" jumps to it). No path or identity is involved, only one of three owner
 * screens; sign-out and any non-owner sign-in clear it.
 */
export const DEEP_LINKS = {
  payments: '/admin/payments',
  business: '/admin/settings/business',
  new_quote: '/admin/quotes/new',
} as const;
export type DeepLinkKey = keyof typeof DEEP_LINKS;

const STORE = 'scopie.deep_link';
const LIFE_MS = 30 * 60_000;
/** The longest a key can live from the moment the link was opened, however often first run renews it. */
const MAX_MS = 2 * 60 * 60_000;

const isKey = (v: unknown): v is DeepLinkKey => typeof v === 'string' && Object.prototype.hasOwnProperty.call(DEEP_LINKS, v);

/** The key of an owner path that a setup email links to (exact match, a trailing slash allowed), else null. Prototype names are not keys. */
export function keyForPath(pathname: string): DeepLinkKey | null {
  const p = pathname.length > 1 ? pathname.replace(/\/$/, '') : pathname;
  return (Object.keys(DEEP_LINKS) as DeepLinkKey[]).find((k) => DEEP_LINKS[k] === p) ?? null;
}

/** `first` is when the link was opened; renewals keep it, a newly opened link sets it to now. */
export function stashDeepLinkKey(key: DeepLinkKey, now = Date.now(), first = now): void {
  if (!isKey(key)) return;
  try {
    sessionStorage.setItem(STORE, JSON.stringify({ key, at: now, first }));
  } catch {
    // storage blocked: the owner lands on their home page and opens the step from the setup card
  }
}

/** Keeps the key for `pathname` when it is one of the three links; any other path stores nothing (and leaves an earlier key as it is). */
export function stashDeepLinkForPath(pathname: string): void {
  const key = keyForPath(pathname);
  if (key) stashDeepLinkKey(key);
}

function readEntry(now: number): { key: DeepLinkKey; first: number } | null {
  try {
    const v = JSON.parse(sessionStorage.getItem(STORE) ?? 'null') as { key?: unknown; at?: unknown; first?: unknown } | null;
    if (!v || !isKey(v.key) || typeof v.at !== 'number' || now - v.at < 0 || now - v.at >= LIFE_MS) return null;
    // an entry without a usable first time counts from its last time, so it can only be shorter
    const first = typeof v.first === 'number' && v.first <= v.at ? v.first : v.at;
    if (now - first >= MAX_MS) return null;
    return { key: v.key, first };
  } catch {
    // unreadable: nothing stored
  }
  return null;
}

const read = (now: number): DeepLinkKey | null => readEntry(now)?.key ?? null;

/**
 * A waiting key lives on while the owner is in first run (the 30 minutes count from the last visit there), so a slow first run, or a trip out with the back
 * arrow and a return, does not lose it. Never beyond 2 hours from the moment the link was opened, however many visits renew it.
 */
export function touchDeepLink(now = Date.now()): void {
  const e = readEntry(now);
  if (e) stashDeepLinkKey(e.key, now, e.first);
}

export function clearDeepLink(): void {
  try {
    sessionStorage.removeItem(STORE);
  } catch {
    // nothing to do
  }
}

/** The path to open now, once (the key is removed), or null. */
export function takeDeepLinkPath(now = Date.now()): string | null {
  const key = read(now);
  clearDeepLink();
  return key ? DEEP_LINKS[key] : null;
}
